import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Trash2 } from 'lucide-react';
import { usePiercingWorkflow, PiercingAct } from '../contexts/PiercingWorkflowContext';
import PiercingWorkflowStepper from './PiercingWorkflowStepper';
import { apiClient } from '../utils/api';
import { ConfirmDefinitiveDeleteModal } from '../components/ConfirmDefinitiveDeleteModal';
import { useToast } from '../components/Toast';

interface BodyZone { id: string; name: string; }
interface PiercingTypeModel { id: string; name: string; bodyZoneId: string; }

interface EditingAct {
  actId: string;
  bodyZoneId: string;
  piercingTypeModelId: string;
  jewelryMaterial: string;
  jewelryOption: string;
  notes: string;
}

const JEWELRY_MATERIALS = ['Titane G23', 'Or 14k', 'Or 18k', 'Acier chirurgical', 'Niobium'];
const JEWELRY_OPTIONS   = ['Anneau', 'Barbell', 'Labret', 'Captif', 'Strass', 'Autre'];

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}
function formatTime(iso: string) {
  return new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}
function isMinorFromBirthDate(birthDate?: string) {
  if (!birthDate) return false;
  return (Date.now() - new Date(birthDate).getTime()) / (1000 * 60 * 60 * 24 * 365.25) < 18;
}

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const map: Record<string, { label: string; cls: string }> = {
    PLANNED:     { label: 'Planifié',  cls: 'bg-gray-100 text-gray-600'  },
    IN_PROGRESS: { label: 'En cours',  cls: 'bg-blue-100 text-blue-700'  },
    COMPLETED:   { label: 'Terminé',   cls: 'bg-green-100 text-green-700' },
    CANCELLED:   { label: 'Annulé',    cls: 'bg-red-100 text-red-600'    },
  };
  const { label, cls } = map[status] ?? { label: status, cls: 'bg-gray-100 text-gray-500' };
  return <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${cls}`}>{label}</span>;
};

const PiercingDetailsPage: React.FC = () => {
  const navigate = useNavigate();
  const { appointmentId } = useParams<{ appointmentId: string }>();
  const { appointment, acts, loading, error, reload, updateActLocal, appendActLocal, removeActLocal } = usePiercingWorkflow();
  const { showToast, ToastComponent } = useToast();

  const [bodyZones, setBodyZones]       = useState<BodyZone[]>([]);
  const [piercingTypes, setPiercingTypes] = useState<PiercingTypeModel[]>([]);
  const [editingAct, setEditingAct]     = useState<EditingAct | null>(null);
  const [saving, setSaving]             = useState(false);
  const [addingAct, setAddingAct]       = useState(false);
  const [deletingActId, setDeletingActId] = useState<string | null>(null);
  const [actPendingDelete, setActPendingDelete] = useState<PiercingAct | null>(null);
  const [validating, setValidating]     = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [zr, tr] = await Promise.all([
          apiClient('/api/practitioner/body-zones'),
          apiClient('/api/practitioner/piercing-types'),
        ]);
        if (zr.ok) { const d = await zr.json(); setBodyZones(d.data?.bodyZones ?? []); }
        if (tr.ok) { const d = await tr.json(); setPiercingTypes(d.data?.piercingTypes ?? []); }
      } catch { /* catalog optional */ }
    })();
  }, []);

  const openEdit = (act: PiercingAct) => {
    const dd = act.demandDetails ?? {};
    setEditingAct({
      actId: act.id,
      bodyZoneId:           dd.bodyZoneId ?? act.piercingRecord?.bodyZone?.id ?? '',
      piercingTypeModelId:  dd.piercingTypeModelId ?? act.piercingRecord?.piercingTypeModel?.id ?? '',
      jewelryMaterial:      (dd.jewelry as any)?.material ?? '',
      jewelryOption:        (dd.jewelry as any)?.option ?? '',
      notes:                dd.notes ?? '',
    });
  };

  const saveEdit = async () => {
    if (!editingAct) return;
    setSaving(true);
    try {
      const res = await apiClient(`/api/practitioner/acts/${editingAct.actId}/demand-details`, {
        method: 'PUT',
        body: JSON.stringify({
          bodyZoneId:          editingAct.bodyZoneId || null,
          piercingTypeModelId: editingAct.piercingTypeModelId || null,
          jewelry: { material: editingAct.jewelryMaterial, option: editingAct.jewelryOption },
          notes:               editingAct.notes || null,
        }),
      });
      if (!res.ok) throw new Error('Erreur de sauvegarde');
      updateActLocal(editingAct.actId, {
        demandDetails: {
          bodyZoneId:          editingAct.bodyZoneId || undefined,
          piercingTypeModelId: editingAct.piercingTypeModelId || undefined,
          jewelry: { material: editingAct.jewelryMaterial, option: editingAct.jewelryOption },
          notes:               editingAct.notes || undefined,
        },
      });
      setEditingAct(null);
    } catch (err: any) {
      alert(err.message || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleAddAct = async () => {
    if (!appointmentId) return;
    setAddingAct(true);
    try {
      const res = await apiClient(`/api/practitioner/appointments/${appointmentId}/acts`, {
        method: 'POST',
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        alert((body as { message?: string }).message || "Impossible d'ajouter un acte");
        return;
      }
      const created = (body as { data?: { act?: PiercingAct } }).data?.act;
      if (created) appendActLocal(created);
      await reload();
    } catch {
      alert("Erreur lors de l'ajout de l'acte");
    } finally {
      setAddingAct(false);
    }
  };

  const getBodyZoneName = (act: PiercingAct) => {
    // demandDetails has priority: it reflects the latest user edit
    const ddId = act.demandDetails?.bodyZoneId;
    if (ddId) {
      const found = bodyZones.find((z) => z.id === ddId)?.name;
      if (found) return found;
    }
    return act.piercingRecord?.bodyZone?.name ?? '—';
  };

  const getPiercingTypeName = (act: PiercingAct) => {
    const ddId = act.demandDetails?.piercingTypeModelId;
    if (ddId) {
      const found = piercingTypes.find((t) => t.id === ddId)?.name;
      if (found) return found;
    }
    return act.piercingRecord?.piercingTypeModel?.name ?? '—';
  };

  const executeDeleteAct = async (act: PiercingAct) => {
    setDeletingActId(act.id);
    try {
      const res = await apiClient(`/api/practitioner/acts/${act.id}`, { method: 'DELETE' });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        showToast((body as { message?: string }).message || "Impossible de supprimer l'acte", 'error', 4500);
        return;
      }
      if (editingAct?.actId === act.id) setEditingAct(null);
      removeActLocal(act.id);
      setActPendingDelete(null);
      showToast(`Acte #${act.sequence} et images associées supprimés`, 'success', 2800);
      await reload();
    } catch {
      showToast("Erreur lors de la suppression de l'acte", 'error', 4500);
    } finally {
      setDeletingActId(null);
    }
  };

  const handleValidate = async () => {
    if (!appointmentId) return;
    setValidating(true);
    try {
      navigate(`/practitioner/piercing/${appointmentId}/contract`);
    } finally {
      setValidating(false);
    }
  };

  const isMinor = isMinorFromBirthDate(appointment?.clientBirthDate);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
      </div>
    );
  }

  if (error || !appointment) {
    return (
      <div className="card text-center py-10 text-red-600">
        <p>{error ?? 'Rendez-vous introuvable.'}</p>
        <button className="btn-primary-sm mt-4" onClick={reload}>Réessayer</button>
      </div>
    );
  }

  return (
    <>
      <ToastComponent />
      <PiercingWorkflowStepper currentStep={0} />

      {isMinor && (
        <div className="mb-6 p-4 bg-amber-50 border border-amber-300 rounded-lg text-amber-800 flex items-center gap-3">
          <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
            <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" />
          </svg>
          <span className="text-sm font-medium">Client mineur — Le représentant légal doit être présent et signer le contrat.</span>
        </div>
      )}

      <div className="grid grid-cols-12 gap-6">

        {/* ── Left : appointment card ─────────────────────────────────── */}
        <div className="col-span-12 lg:col-span-4 card flex flex-col gap-5">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-bold">Rendez-vous</h2>
            <button
              onClick={reload}
              title="Actualiser"
              className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.65 6.35A7.958 7.958 0 0 0 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08A5.99 5.99 0 0 1 12 18c-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z" />
              </svg>
            </button>
          </div>

          <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
            <div>
              <p className="text-xs uppercase tracking-wider text-gray-400 font-semibold mb-0.5">Date</p>
              <p className="font-medium capitalize text-sm leading-tight">{formatDate(appointment.scheduledAt)}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-gray-400 font-semibold mb-0.5">Heure</p>
              <p className="font-medium">{formatTime(appointment.scheduledAt)}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-gray-400 font-semibold mb-0.5">Client</p>
              <p className="font-medium">{appointment.clientFirstName} {appointment.clientLastName}</p>
              {isMinor && (
                <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-medium">Mineur</span>
              )}
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-gray-400 font-semibold mb-0.5">Boutique</p>
              <p className="font-medium">{appointment.shopName}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-gray-400 font-semibold mb-0.5">Service</p>
              <p className="font-medium">{appointment.serviceName}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-gray-400 font-semibold mb-0.5">Source</p>
              <p className="font-medium">
                {appointment.bookingSource === 'ONLINE'   ? 'En ligne'  :
                 appointment.bookingSource === 'WALK_IN'  ? 'Sans RDV'  : 'Backoffice'}
              </p>
            </div>
            {(appointment.pricing as any)?.basePrice && (
              <div className="col-span-2">
                <p className="text-xs uppercase tracking-wider text-gray-400 font-semibold mb-0.5">Prix de base</p>
                <p className="font-medium">{(appointment.pricing as any).basePrice} €</p>
              </div>
            )}
          </div>

          {appointment.notes && (
            <div>
              <p className="text-xs uppercase tracking-wider text-gray-400 font-semibold mb-1">Notes</p>
              <p className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3 leading-relaxed">{appointment.notes}</p>
            </div>
          )}
        </div>

        {/* ── Right : acts list ────────────────────────────────────────── */}
        <div className="col-span-12 lg:col-span-8">
          <div className="card">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h2 className="text-base font-bold">Actes à réaliser</h2>
                <p className="text-xs text-gray-400 mt-0.5">
                  {acts.length} acte{acts.length > 1 ? 's' : ''} planifié{acts.length > 1 ? 's' : ''}
                </p>
              </div>
              <button
                className="btn-secondary-sm flex items-center gap-1.5"
                onClick={handleAddAct}
                disabled={addingAct}
              >
                {addingAct ? (
                  <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-current" />
                ) : (
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
                  </svg>
                )}
                Ajouter un acte
              </button>
            </div>

            <div className="space-y-3">
              {acts.map((act) => {
                const bodyZone    = getBodyZoneName(act);
                const pType       = getPiercingTypeName(act);
                const jewelry     = act.demandDetails?.jewelry as any;
                const hasContract = (act.contracts ?? []).some((c) => c.status === 'SIGNED');

                return (
                  <div
                    key={act.id}
                    className="flex items-start justify-between gap-4 p-4 bg-gray-50 rounded-xl border border-gray-100"
                  >
                    <div className="flex-1 min-w-0 grid grid-cols-2 gap-x-6 gap-y-1">

                      {/* Row 1 */}
                      <div className="col-span-2 flex items-center gap-2 mb-1">
                        <span className="text-xs font-bold text-gray-400">#{act.sequence}</span>
                        <StatusBadge status={act.status} />
                        {hasContract && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-green-50 text-green-600 font-semibold">
                            Contrat signé
                          </span>
                        )}
                      </div>

                      {/* Type */}
                      <div>
                        <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold mb-0.5">Type</p>
                        <p className="text-sm font-semibold">{pType}</p>
                      </div>

                      {/* Zone */}
                      <div>
                        <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold mb-0.5">Zone</p>
                        <p className="text-sm font-semibold">{bodyZone}</p>
                      </div>

                      {/* Bijou */}
                      {(jewelry?.material || jewelry?.option) && (
                        <div className="col-span-2">
                          <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold mb-0.5">Bijou</p>
                          <p className="text-sm text-gray-700">
                            {[jewelry.option, jewelry.material].filter(Boolean).join(' · ')}
                          </p>
                        </div>
                      )}

                      {/* Placement */}
                      {act.demandDetails?.placement && Object.keys(act.demandDetails.placement).length > 0 && (
                        <div className="col-span-2">
                          <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold mb-0.5">Placement</p>
                          <p className="text-sm text-gray-700">
                            {JSON.stringify(act.demandDetails.placement)}
                          </p>
                        </div>
                      )}

                      {/* Notes */}
                      {act.demandDetails?.notes && (
                        <div className="col-span-2">
                          <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold mb-0.5">Notes</p>
                          <p className="text-sm text-gray-600 italic">{act.demandDetails.notes}</p>
                        </div>
                      )}

                      {/* Guardian info if minor */}
                      {(act.demandDetails?.isMinor || isMinor) &&
                        (act.demandDetails?.guardianFirstName || act.demandDetails?.guardianLastName) && (
                          <div className="col-span-2 mt-1">
                            <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold mb-0.5">
                              Représentant légal
                            </p>
                            <p className="text-sm text-amber-700 font-medium">
                              {act.demandDetails.guardianFirstName} {act.demandDetails.guardianLastName}
                            </p>
                          </div>
                      )}
                    </div>

                    <div className="flex flex-col gap-2 flex-shrink-0 mt-1">
                      <button
                        className="btn-secondary-sm"
                        onClick={() => openEdit(act)}
                      >
                        Modifier
                      </button>
                      <button
                        type="button"
                        className="btn-secondary-sm text-red-600 border-red-200 hover:bg-red-50 flex items-center justify-center gap-1.5 disabled:opacity-50"
                        onClick={() => setActPendingDelete(act)}
                        disabled={deletingActId === act.id || act.status === 'COMPLETED'}
                        title={act.status === 'COMPLETED' ? 'Acte terminé — suppression impossible' : 'Supprimer cet acte et ses images'}
                      >
                        {deletingActId === act.id ? (
                          <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-current" />
                        ) : (
                          <Trash2 className="w-3.5 h-3.5" />
                        )}
                        Supprimer
                      </button>
                    </div>
                  </div>
                );
              })}

              {acts.length === 0 && (
                <div className="text-center py-8 text-gray-400">
                  <svg className="w-10 h-10 mx-auto mb-2 opacity-30" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 14l-5-5 1.41-1.41L12 14.17l7.59-7.59L21 8l-9 9z" />
                  </svg>
                  <p className="text-sm">Aucun acte défini — cliquez sur « Ajouter un acte »</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Edit act modal ────────────────────────────────────────────── */}
      {editingAct && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl">
            <h3 className="text-lg font-bold mb-5">Modifier l'acte</h3>
            <div className="space-y-4">
              <div>
                <label className="text-xs uppercase tracking-wider text-gray-500 font-semibold block mb-1">Zone corporelle</label>
                <select className="input w-full" value={editingAct.bodyZoneId}
                  onChange={(e) => {
                    const newZoneId = e.target.value;
                    // Reset piercing type when zone changes so we never keep a type from a different zone
                    const currentTypeStillValid = piercingTypes.some(
                      (t) => t.id === editingAct.piercingTypeModelId && t.bodyZoneId === newZoneId,
                    );
                    setEditingAct({
                      ...editingAct,
                      bodyZoneId: newZoneId,
                      piercingTypeModelId: currentTypeStillValid ? editingAct.piercingTypeModelId : '',
                    });
                  }}>
                  <option value="">Sélectionner...</option>
                  {bodyZones.map((z) => <option key={z.id} value={z.id}>{z.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-gray-500 font-semibold block mb-1">
                  Type de piercing
                  {editingAct.bodyZoneId && (
                    <span className="ml-2 normal-case font-normal text-gray-400">
                      — {piercingTypes.filter((t) => t.bodyZoneId === editingAct.bodyZoneId).length} disponible(s)
                    </span>
                  )}
                </label>
                <select
                  className="input w-full"
                  value={editingAct.piercingTypeModelId}
                  disabled={!editingAct.bodyZoneId}
                  onChange={(e) => setEditingAct({ ...editingAct, piercingTypeModelId: e.target.value })}
                >
                  <option value="">{editingAct.bodyZoneId ? 'Sélectionner...' : '← Choisissez d\'abord une zone'}</option>
                  {piercingTypes
                    .filter((t) => !editingAct.bodyZoneId || t.bodyZoneId === editingAct.bodyZoneId)
                    .map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-gray-500 font-semibold block mb-1">Matériau du bijou</label>
                <select className="input w-full" value={editingAct.jewelryMaterial}
                  onChange={(e) => setEditingAct({ ...editingAct, jewelryMaterial: e.target.value })}>
                  <option value="">Sélectionner...</option>
                  {JEWELRY_MATERIALS.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-gray-500 font-semibold block mb-1">Type de bijou</label>
                <select className="input w-full" value={editingAct.jewelryOption}
                  onChange={(e) => setEditingAct({ ...editingAct, jewelryOption: e.target.value })}>
                  <option value="">Sélectionner...</option>
                  {JEWELRY_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-gray-500 font-semibold block mb-1">Notes</label>
                <textarea className="input w-full" rows={2} value={editingAct.notes}
                  onChange={(e) => setEditingAct({ ...editingAct, notes: e.target.value })}
                  placeholder="Notes spécifiques..." />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button className="btn-secondary flex-1" onClick={() => setEditingAct(null)} disabled={saving}>Annuler</button>
              <button className="btn-primary flex-1" onClick={saveEdit} disabled={saving}>
                {saving ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Sticky bottom nav (consistent across all steps) ───────────── */}
      <div className="fixed bottom-0 left-0 w-full bg-white/80 backdrop-blur border-t border-gray-200 px-6 py-4">
        <div className="max-w-5xl mx-auto flex gap-3">
          <button
            className="btn-secondary h-14 px-8 flex items-center gap-2 flex-shrink-0"
            onClick={() => navigate('/practitioner/dashboard')}
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
              <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
            </svg>
            Tableau de bord
          </button>
          <button
            className="btn-primary h-14 flex-1 flex items-center justify-center gap-2 disabled:opacity-50"
            onClick={handleValidate}
            disabled={validating || acts.length === 0}
          >
            <span className="font-bold text-sm uppercase tracking-widest">
              {validating ? 'Chargement...' : 'Continuer le traitement'}
            </span>
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z" />
            </svg>
          </button>
        </div>
      </div>

      <div className="pb-28" />

      <ConfirmDefinitiveDeleteModal
        isOpen={actPendingDelete !== null}
        count={1}
        entityLabel={
          actPendingDelete
            ? `acte #${actPendingDelete.sequence} (${getPiercingTypeName(actPendingDelete)} — ${getBodyZoneName(actPendingDelete)}), y compris toutes les photos, le contrat et les documents associés`
            : 'acte'
        }
        isLoading={!!deletingActId}
        onClose={() => {
          if (deletingActId) return;
          setActPendingDelete(null);
        }}
        onConfirm={() => {
          if (!actPendingDelete) return;
          void executeDeleteAct(actPendingDelete);
        }}
      />
    </>
  );
};

export default PiercingDetailsPage;
